/**
 * Hook into Alpine so that it is available no matter at which point Alpine loads.
 */
document.addEventListener('alpine:init', () => {
    /**
     * Manages toast notifications across ASAPConnect.
     */
    Alpine.store('toast', {
        /**
         * @var element quick access to the toast notifcation template
         */
        template: null,

        /**
         * @var element quick access to the toast-notification display
         */
        toaster: null,

        /**
         * @var array list of all toast notifications currently being displayed
         */
        toasts: [],

        /**
         * @var int maximum number of toast notifications allowed to be displayed at once
         */
        threshold: 3,

        /**
         * @var object durations in milliseconds for toast actions
         */
        durations: {
            delay: 250,
            load:  250,
            toast: 5000,
            burn:  500,
        },

        /**
         * Set any prerequisites.
         *
         * @return void
         */
        init() {
            this.template = document.getElementById('toast-template');
            this.toaster  = document.getElementById('toast-display');

            Livewire.on('toast', ({message, level = constants.notice}) => this.notify(message, level));
        },

        /**
         * Display a toast notification.
         *
         * @param string message what to display in the toast (raw html accepted)
         * @param string level   optional level to display the toast notification as
         *
         * @return void
         */
        async notify(message, level = constants.notice) {
            let toast = this.create(message, level);

            await this.wait(this.durations.delay);
            await this.load(toast);
            await this.wait(this.durations.toast);
            await this.burn(toast);

            this.eject(toast);
        },

        /**
         * Create a toast notification element.
         *
         * @param string message what to display in the toast (raw html accepted)
         * @param string level   optional level to display the toast notification as
         *
         * @return element
         */
        create(message, level = constants.notice) {
            let toast = this.template.cloneNode(true);

            let id = 'toast-notification-'+(this.toasts.length);

            toast.setAttribute('id', id);
            toast.classList.add('toast-'+level);

            toast.getElementsByClassName('toast-message')[0].innerHTML = message;

            return toast;
        },

        /**
         * Wait a certain amount of time before the next action.
         *
         * @param int time how long in milliseconds to wait
         *
         * @return Promise
         */
        async wait(time) {
            return new Promise(resolve => setTimeout(resolve, time));
        },

        /**
         * Animate the loading of the notification using the FLIP technique.
         *
         * @param element toast to animate
         *
         * @return Promise
         */
        async load(toast) {
            this.toasts.push(toast.id);

            let first = this.toaster.offsetHeight;
            this.toaster.prepend(toast);
            let last   = this.toaster.offsetHeight;
            let invert = (last - first);

            this.toaster.animate([
                { transform: 'translateY(-'+invert+'px)' },
                { transform: 'translateY(0)' },
            ], {
                duration: this.durations.load,
                easing: 'ease-out',
            });

            this.clean();

            await Promise.allSettled(toast.getAnimations().map(animation => animation.finished));
        },

        /**
         * Perform a fade animation on the toast notification.
         *
         * @param element toast to hide
         *
         * @return Promise
         */
        async burn(toast) {
            toast.animate([
                { opacity: 100, visibility: 'visible' },
                { opacity: 0, visibility: 'hidden' },
            ], {
                duration: this.durations.burn,
                easing: 'ease-in-out',
            });

            await Promise.allSettled(toast.getAnimations().map(animation => animation.finished));
        },

        /**
         * Eject the toast notification from the DOM.
         *
         * @param element toast the notification to remove
         *
         * @return void
         */
        eject(toast) {
            let index = this.toasts.findIndex(match => match === toast.id);
            if ((index in this.toasts) && (this.toasts[index] === toast.id)) {
                this.toasts.splice(index, 1);
            }

            if (this.toaster.contains(toast)) {
                this.toaster.removeChild(toast);
            }
        },

        /**
         * Clean out any toast notifications over the threshold.
         *
         * @return void
         */
        clean() {
            if (this.toasts.length > this.threshold) {
                let removed = 0;

                for (let index in this.toasts) {
                    if (removed >= (this.toasts.length - this.threshold)) {
                        break;
                    }

                    let toast = this.toaster.querySelector('#'+this.toasts[index]);
                    this.eject(toast);
                    removed++;
                }
            }
        },
    });
});
